import { useCallback, useEffect, useState } from 'react';
import { isAppSwitcherEnabled } from '@sas/nova-core/app-switcher';
import { RETURN as AppReturnEvent } from '@sas/nova-core/app-switcher/events/ApplicationSwitcherObservable';
import { getLogger } from '@sas/nova-core/logging';
const logger = getLogger('Avatar');
const getAvatarKey = (id, type = 'user') => `${id}|${type}`;
const onUpdateListenersMap = new Map();
const updateAvatar = (id, type = 'user') => {
  const avatarKey = getAvatarKey(id, type);
  const avatarOnUpdateListeners = onUpdateListenersMap.get(avatarKey);
  if (!avatarOnUpdateListeners) return;
  const currentTime = Date.now();
  avatarOnUpdateListeners.lastModifiedTime = currentTime;
  if (!avatarOnUpdateListeners.listeners) return;
  for (const onUpdateListener of avatarOnUpdateListeners.listeners) {
    onUpdateListener(currentTime);
  }
};
export const usePublishAvatarUpdate = (dataProvider, id, type) => {
  const actualId = useUserGroupActualId(dataProvider, id, type);
  return () => {
    if (!actualId) return;
    updateAvatar(actualId, type);
  };
};
export const useOnAvatarUpdate = (dataProvider, onUpdate, id, type, fetchedTime) => {
  const actualId = useUserGroupActualId(dataProvider, id, type);
  const onUpdateListener = useCallback(lastModifiedTime => {
    if (!fetchedTime || lastModifiedTime > fetchedTime) {
      onUpdate();
    }
  }, [fetchedTime, onUpdate]);
  const avatarKey = getAvatarKey(actualId, type);
  useEffect(() => {
    if (!fetchedTime) {
      return;
    }
    const avatarOnUpdateListeners = onUpdateListenersMap.get(avatarKey);
    const lastModifiedTime = avatarOnUpdateListeners?.lastModifiedTime;
    if (lastModifiedTime && lastModifiedTime > fetchedTime) {
      onUpdate();
    }
  }, [fetchedTime, avatarKey, onUpdate]);
  useEffect(() => {
    if (!onUpdateListenersMap.has(avatarKey)) {
      onUpdateListenersMap.set(avatarKey, {
        listeners: []
      });
    }
    const {
      listeners
    } = onUpdateListenersMap.get(avatarKey);
    listeners.push(onUpdateListener);
    return () => {
      const avatarOnUpdateListeners = onUpdateListenersMap.get(avatarKey);
      avatarOnUpdateListeners.listeners = avatarOnUpdateListeners.listeners.filter(listener => listener !== onUpdateListener);
    };
  }, [avatarKey, onUpdateListener]);

  // Update the avatar if the application returns from the background to the foreground
  // to sync with any changes made in another application such as updating profile picture
  useEffect(() => {
    if (!isAppSwitcherEnabled()) return;
    let applicationSwitcher;
    import('../../NovaApplicationSwitcher').then(module => {
      applicationSwitcher = module.applicationSwitcher;
      applicationSwitcher.subscribe(AppReturnEvent, onUpdate);
    });
    return () => {
      applicationSwitcher?.unsubscribe(AppReturnEvent, onUpdate);
    };
  }, [onUpdate]);
};

// There are cases where id is different from its actual id.
// For example, "@currentUser" is a special id for Viya and its actual id is dependent on the current user signed in
export const useUserGroupActualId = (dataProvider, id, type) => {
  const [actualId, setActualId] = useState(id);
  useEffect(() => {
    let ignoreResult = false;
    (async () => {
      if (!dataProvider.resolveId) {
        setActualId(id);
        return;
      }
      try {
        const actualId = await dataProvider.resolveId(id, type);
        if (ignoreResult) return;
        setActualId(actualId);
      } catch (e) {
        if (ignoreResult) return;
        setActualId(id);
        logger.error(`Failed to get the actual id for id:${id} and type: ${type}`);
      }
    })();
    return () => {
      ignoreResult = true;
    };
  }, [dataProvider, id, type]);
  return actualId ?? '';
};
//# sourceMappingURL=useOnAvatarUpdate.js.map